import type { Edge, Node } from '@xyflow/react';
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import * as Api from '@ViewModels';
import type { BaseNodeData } from '../../../../web/containers/AutomationFlows/types';

import type { TestContact } from '../testing/types';

const EXCLUDED_STEP_TYPES: string[] = [];

/**
 * useCanvasState - Owns the local React state that the canvas needs.
 *
 * This is a clean extraction from AutomationCanvasEditor: all local
 * useState / useMemo / useCallback calls and the nodesRef sync effect.
 */
export function useCanvasState() {
	const [nodes, setNodes] = useState<Node<BaseNodeData>[]>([]);
	const [edges, setEdges] = useState<Edge[]>([]);
	const [testContacts, setTestContacts] = useState<TestContact[]>([]);
	const [isInitialized, setIsInitialized] = useState(false);
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const [canvasSyncVersion, setCanvasSyncVersion] = useState(0);

	// Do not exclude switch globally — eligibility is target-template specific and
	// enforced when adding/dropping steps.
	const excludedStepTypes = EXCLUDED_STEP_TYPES;

	// Ref kept in sync with nodes state for use in callbacks/effects.
	const nodesRef = useRef<Node<BaseNodeData>[]>([]);
	useEffect(() => {
		nodesRef.current = nodes;
	}, [nodes]);

	// Derived: selected node and selected switch node ID.
	const selectedNode = useMemo(() => {
		if (!selectedNodeId) {
			return null;
		}
		return nodes.find(n => n.id === selectedNodeId) ?? null;
	}, [selectedNodeId, nodes]);

	const selectedSwitchNodeId = useMemo(() => {
		if (!selectedNode) {
			return null;
		}
		const data = selectedNode.data;
		if ('stepType' in data && data.stepType === Api.AutomationStepType.Switch) {
			return selectedNode.id;
		}
		return null;
	}, [selectedNode]);

	// Change handlers.
	const handleNodesChange = useCallback((updatedNodes: Node<BaseNodeData>[]) => {
		setNodes(updatedNodes);
	}, []);

	const handleEdgesChange = useCallback((updatedEdges: Edge[]) => {
		setEdges(updatedEdges);
	}, []);

	const handleTestContactsChange = useCallback((nextContacts: SetStateAction<TestContact[]>) => {
		setTestContacts(nextContacts);
	}, []);

	const handleSelectionChange = useCallback((selectedNodeIds: string[]) => {
		setSelectedNodeId(selectedNodeIds.length > 0 ? selectedNodeIds[0] : null);
	}, []);

	return {
		nodes,
		setNodes,
		edges,
		setEdges,
		testContacts,
		setTestContacts,
		isInitialized,
		setIsInitialized,
		selectedNodeId,
		setSelectedNodeId,
		canvasSyncVersion,
		setCanvasSyncVersion,
		excludedStepTypes,
		nodesRef,
		selectedNode,
		selectedSwitchNodeId,
		handleNodesChange,
		handleEdgesChange,
		handleTestContactsChange,
		handleSelectionChange,
	};
}
